import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";

import {Button} from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {useToast} from "@/src/modules/core/hooks";
import {AxiosError} from "axios";

import {PasswordInput} from "./password-input";
import {useChangePassword} from "../hooks";
import {changePasswordSchema} from "../schemas";
import {useSessionStore} from "../store/session";

export function ChangePasswordModal() {
  const toast = useToast();
  const session = useSessionStore((state) => state.session);
  const setSession = useSessionStore((state) => state.setSession);
  const [apiPasswordError, setApiPasswordError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof changePasswordSchema>>({
    defaultValues: {
      password: "",
      password_confirm: "",
    },
    resolver: zodResolver(changePasswordSchema),
  });

  const {mutateAsync: changePassword, isPending} = useChangePassword();

  const handleSubmit = async (data: z.infer<typeof changePasswordSchema>) => {
    // Clear previous API errors
    setApiPasswordError(null);

    try {
      await changePassword({password: data.password});

      // Update session to set requires_password_change to false
      if (session) {
        setSession({
          ...session,
          requires_password_change: false,
        });
      }

      toast.success("Password changed successfully");
    } catch (error) {
      // Extract error message from API response
      let errorMessage = "Failed to change password";
      let passwordError: string | null = null;

      if (error instanceof AxiosError) {
        const responseData = error.response?.data as any;
        
        // Use the message field from the API response
        if (responseData?.message) {
          errorMessage = responseData.message;
        }
        
        // Extract password error from errors.password array
        if (responseData?.errors?.password && Array.isArray(responseData.errors.password) && responseData.errors.password.length > 0) {
          passwordError = responseData.errors.password[0];
          setApiPasswordError(passwordError);
        }
      } else if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  const isOpen = session?.requires_password_change === true;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl">Change Password</DialogTitle>
          <DialogDescription className="text-center">
            You must change your password before continuing
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="password"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} disabled={isPending} />
                  </FormControl>
                  {formState.errors.password && (
                    <FormError error={formState.errors.password.message} />
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password_confirm"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} disabled={isPending} />
                  </FormControl>
                  {formState.errors.password_confirm && (
                    <FormError error={formState.errors.password_confirm.message} />
                  )}
                  {apiPasswordError && (
                    <p className="text-sm text-red-600 mt-1">{apiPasswordError}</p>
                  )}
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  Changing password...
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                "Change Password"
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
